import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, EditorState } from '@tiptap/pm/state';
import { Decoration, DecorationSet, EditorView } from '@tiptap/pm/view';
import type { Node as PMNode } from '@tiptap/pm/model';
import { detectLanguage, LANG_TAG, SpellLanguage } from '../spellcheck/language';
import { dictionaryFailed, getSpeller, loadSpeller } from '../spellcheck/dictionaries';
import { findWords, isMisspelled } from '../spellcheck/words';

/**
 * Spellcheck with our own dictionaries instead of the browser's. A browser
 * only checks against the dictionaries installed on that machine (Firefox
 * ships just one), so a Spanish document on an English Firefox comes out
 * all red. Like Google Docs, we turn the browser's checker off and do it
 * ourselves: each paragraph's language is guessed from its words, checked
 * against that language's dictionary, and the misspellings get a red
 * underline with suggestions on right-click.
 */

export interface SpellcheckOptions {
    enabled: boolean;
    /** The interface language, for documents too short to tell */
    fallbackLanguage: SpellLanguage;
    customDictionary: string[];
    onAddToDictionary?: (word: string) => void;
    labels: { addToDictionary: string; ignore: string; noSuggestions: string };
}

interface SpellState {
    decorations: DecorationSet;
    lang: SpellLanguage;
    /** Hand spelling back to the browser (our dictionary couldn't load) */
    native: boolean;
}

interface MisspellingSpec {
    word: string;
    lang: SpellLanguage;
}

export const spellcheckKey = new PluginKey<SpellState>('spellcheck');

// Words dismissed with "Ignore" stay ignored until the page reloads
const ignored = new Set<string>();
const RECHECK_DELAY_MS = 300;

interface Block {
    text: string;
    /** Document position of each character in `text` */
    posAt: number[];
}

/**
 * The text of every paragraph, heading and list item, with code left out.
 * Inline code becomes spaces and images or line breaks become a newline, so
 * each character still lines up with its spot in the document.
 */
function collectBlocks(doc: PMNode): Block[] {
    const blocks: Block[] = [];
    doc.descendants((node, pos) => {
        if (node.type.spec.code) return false;
        if (!node.isTextblock) return true;
        let text = '';
        const posAt: number[] = [];
        node.descendants((child, offset) => {
            const at = pos + 1 + offset;
            if (child.isText) {
                const isCode = child.marks.some((m) => m.type.spec.code);
                const t = child.text ?? '';
                text += isCode ? ' '.repeat(t.length) : t;
                for (let i = 0; i < t.length; i++) posAt.push(at + i);
            } else if (child.isLeaf) {
                text += '\n';
                posAt.push(at);
            }
            return true;
        });
        blocks.push({ text, posAt });
        return false;
    });
    return blocks;
}

function capitalizeLike(model: string, suggestion: string): string {
    if (/^\p{Lu}/u.test(model) && /^\p{Ll}/u.test(suggestion)) {
        return suggestion.charAt(0).toUpperCase() + suggestion.slice(1);
    }
    return suggestion;
}

export const Spellcheck = Extension.create<SpellcheckOptions>({
    name: 'spellcheck',

    addOptions() {
        return {
            enabled: true,
            fallbackLanguage: 'en',
            customDictionary: [],
            onAddToDictionary: undefined,
            labels: { addToDictionary: 'Add to dictionary', ignore: 'Ignore', noSuggestions: 'No suggestions' },
        };
    },

    addProseMirrorPlugins() {
        const extension = this;
        let menu: HTMLElement | null = null;

        const closeMenu = () => {
            menu?.remove();
            menu = null;
            document.removeEventListener('mousedown', onOutsideMouseDown, true);
            document.removeEventListener('keydown', onMenuKey, true);
            window.removeEventListener('scroll', closeMenu, true);
            window.removeEventListener('blur', closeMenu);
        };
        const onOutsideMouseDown = (e: MouseEvent) => {
            if (menu && !menu.contains(e.target as Node)) closeMenu();
        };
        const onMenuKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                closeMenu();
            }
        };

        /** Checks the whole document and publishes the new underlines. */
        const check = (view: EditorView, recheck: () => void): { cursorWordSkipped: boolean } => {
            const { enabled, fallbackLanguage, customDictionary } = extension.options;
            const { state } = view;
            const previous = spellcheckKey.getState(state)!;
            const publish = (next: Omit<SpellState, 'decorations'> & { decorations: Decoration[] }) =>
                view.dispatch(state.tr.setMeta(spellcheckKey, {
                    ...next,
                    decorations: DecorationSet.create(state.doc, next.decorations),
                }).setMeta('addToHistory', false));

            if (!enabled || !view.editable) {
                if (previous.decorations !== DecorationSet.empty || previous.native) {
                    publish({ decorations: [], lang: previous.lang, native: false });
                }
                return { cursorWordSkipped: false };
            }

            const blocks = collectBlocks(state.doc);
            const docLang = detectLanguage(blocks.map((b) => b.text).join('\n')) ?? fallbackLanguage;
            if (!getSpeller(docLang)) loadSpeller(docLang).then(recheck);

            const custom = new Set(customDictionary.map((w) => w.toLowerCase()));
            const { empty, head } = state.selection;
            let cursorWordSkipped = false;
            const decorations: Decoration[] = [];

            for (const block of blocks) {
                const lang = detectLanguage(block.text) ?? docLang;
                const speller = getSpeller(lang);
                if (!speller) {
                    if (!dictionaryFailed(lang)) loadSpeller(lang).then(recheck);
                    continue;
                }
                for (const token of findWords(block.text)) {
                    const lower = token.word.toLowerCase();
                    if (custom.has(lower) || ignored.has(lower)) continue;
                    if (!isMisspelled(token, lang, speller)) continue;
                    const from = block.posAt[token.start];
                    const to = block.posAt[token.end - 1] + 1;
                    // Don't underline a word while it's still being typed
                    if (empty && head >= from && head <= to) {
                        cursorWordSkipped = true;
                        continue;
                    }
                    const spec: MisspellingSpec = { word: token.word, lang };
                    decorations.push(Decoration.inline(from, to, { class: 'spelling-error' }, spec));
                }
            }
            publish({ decorations, lang: docLang, native: dictionaryFailed(docLang) });
            return { cursorWordSkipped };
        };

        const openMenu = (view: EditorView, event: MouseEvent, deco: Decoration) => {
            closeMenu();
            const { word, lang } = deco.spec as MisspellingSpec;
            const { labels, onAddToDictionary } = extension.options;
            const speller = getSpeller(lang);
            const suggestions = Array.from(new Set(
                (speller?.suggest(word) ?? []).slice(0, 5).map((s) => capitalizeLike(word, s)),
            ));

            menu = document.createElement('div');
            menu.className = 'spellcheck-menu';
            menu.setAttribute('role', 'menu');
            const addItem = (label: string, className: string, onPick: () => void) => {
                const button = document.createElement('button');
                button.type = 'button';
                button.className = className;
                button.setAttribute('role', 'menuitem');
                button.textContent = label;
                // mousedown, not click: keep the editor's selection where it is
                button.addEventListener('mousedown', (e) => {
                    e.preventDefault();
                    closeMenu();
                    onPick();
                });
                menu!.appendChild(button);
            };
            const relookup = () => {
                view.dispatch(view.state.tr.setMeta(spellcheckKey, 'recheck'));
                view.focus();
            };

            suggestions.forEach((suggestion) =>
                addItem(suggestion, 'spellcheck-menu-item spellcheck-suggestion', () => {
                    // The decoration may have moved if someone else edited meanwhile
                    const current = spellcheckKey.getState(view.state)?.decorations
                        .find(deco.from, deco.to, (spec) => spec === deco.spec)[0];
                    if (!current) return;
                    view.dispatch(view.state.tr.insertText(suggestion, current.from, current.to));
                    view.focus();
                }));
            if (suggestions.length === 0) {
                const none = document.createElement('div');
                none.className = 'spellcheck-menu-empty';
                none.textContent = labels.noSuggestions;
                menu.appendChild(none);
            }
            menu.appendChild(Object.assign(document.createElement('div'), { className: 'spellcheck-menu-divider' }));
            addItem(labels.addToDictionary, 'spellcheck-menu-item', () => {
                ignored.add(word.toLowerCase());
                onAddToDictionary?.(word);
                relookup();
            });
            addItem(labels.ignore, 'spellcheck-menu-item', () => {
                ignored.add(word.toLowerCase());
                relookup();
            });

            document.body.appendChild(menu);
            // Keep it on screen near the pointer
            const rect = menu.getBoundingClientRect();
            const left = Math.min(event.clientX, window.innerWidth - rect.width - 8);
            const top = event.clientY + rect.height > window.innerHeight - 8
                ? Math.max(8, event.clientY - rect.height)
                : event.clientY;
            menu.style.left = `${Math.max(8, left)}px`;
            menu.style.top = `${top}px`;

            document.addEventListener('mousedown', onOutsideMouseDown, true);
            document.addEventListener('keydown', onMenuKey, true);
            window.addEventListener('scroll', closeMenu, true);
            window.addEventListener('blur', closeMenu);
        };

        return [
            new Plugin<SpellState>({
                key: spellcheckKey,
                state: {
                    init: (): SpellState => ({
                        decorations: DecorationSet.empty,
                        lang: extension.options.fallbackLanguage,
                        native: false,
                    }),
                    apply(tr, value): SpellState {
                        const meta = tr.getMeta(spellcheckKey);
                        if (meta && typeof meta === 'object') return meta as SpellState;
                        if (!tr.docChanged) return value;
                        return { ...value, decorations: value.decorations.map(tr.mapping, tr.doc) };
                    },
                },
                props: {
                    decorations(state: EditorState) {
                        return spellcheckKey.getState(state)?.decorations;
                    },
                    // Our underlines replace the browser's, unless our
                    // dictionary failed to load and the browser is all we have
                    attributes(state: EditorState) {
                        const value = spellcheckKey.getState(state);
                        return {
                            spellcheck: value?.native ? 'true' : 'false',
                            lang: LANG_TAG[value?.lang ?? 'en'],
                        };
                    },
                    handleDOMEvents: {
                        contextmenu(view, event) {
                            const target = event.target as HTMLElement | null;
                            if (!target?.closest?.('.spelling-error')) return false;
                            const at = view.posAtCoords({ left: event.clientX, top: event.clientY });
                            if (!at) return false;
                            const deco = spellcheckKey.getState(view.state)?.decorations
                                .find(at.pos, at.pos, (spec) => Boolean(spec?.word))[0];
                            if (!deco) return false;
                            event.preventDefault();
                            openMenu(view, event, deco);
                            return true;
                        },
                    },
                },
                view(view) {
                    let timer: ReturnType<typeof setTimeout> | undefined;
                    let cursorWordSkipped = false;
                    let destroyed = false;
                    const run = () => {
                        timer = undefined;
                        if (destroyed || view.isDestroyed) return;
                        ({ cursorWordSkipped } = check(view, recheck));
                    };
                    const schedule = (delay: number) => {
                        if (timer) clearTimeout(timer);
                        timer = setTimeout(run, delay);
                    };
                    function recheck() { schedule(0); }
                    schedule(0);

                    return {
                        update(view, prevState) {
                            if (view.state.doc !== prevState.doc) {
                                schedule(RECHECK_DELAY_MS);
                            } else if (!view.state.selection.eq(prevState.selection)) {
                                // The cursor left the word it was typing: check it now
                                if (cursorWordSkipped) schedule(0);
                            } else if (spellcheckKey.getState(view.state) === spellcheckKey.getState(prevState)) {
                                // Settings changed or someone asked for a recheck. Our
                                // own results change the plugin state, so they never land
                                // here and can't set off another pass.
                                schedule(RECHECK_DELAY_MS);
                            }
                        },
                        destroy() {
                            destroyed = true;
                            if (timer) clearTimeout(timer);
                            closeMenu();
                        },
                    };
                },
            }),
        ];
    },
});
